import React from 'react'
import i18n from 'i18n'
import FolderListItem from 'pages/calendar/components/FolderListItem'
import { isOrgSharedAccountEmail } from 'utils/orgSharedAccount'
import styles from './style.module.scss'

interface CalendarColor {
  _cdata: string
}

interface Folder {
  url: string
  displayName: string
  isSync: boolean
  calendarColor: CalendarColor
  isShared: boolean
  isSharedOwner: boolean
  isDefault: boolean
  folderType: string
  owner?: string
}

interface FolderListProps {
  folders: Folder[]
  onCheckClick: (checked: boolean, folderId: string) => void
  onFolderEdit: (folderId: string) => void
  onShareClick: (folderId: string) => void
  onDeleteClick: (folderId: string) => void
  onUnsubscribeClick?: (folderId: string) => void
}

const FolderList: React.FC<FolderListProps> = ({
  folders,
  onCheckClick,
  onFolderEdit,
  onShareClick,
  onDeleteClick,
  onUnsubscribeClick,
}) => {
  const getFolderList = (): React.ReactNode[] => {
    const regularFolders = folders.filter((f) => f.folderType !== 'subscription')
    const subscriptionFolders = folders.filter((f) => f.folderType === 'subscription')

    const myFolders = regularFolders.filter((f) => !f.isShared)
    const sharedFolders = regularFolders.filter(
      (f) => f.isShared && !isOrgSharedAccountEmail(f.owner),
    )
    const orgSharedFolders = regularFolders.filter(
      (f) => f.isShared && isOrgSharedAccountEmail(f.owner),
    )

    const renderFolderItem = (folder: Folder): React.ReactNode => {
      const isOrgShared = folder.isShared && isOrgSharedAccountEmail(folder.owner)
      return (
        <FolderListItem
          key={folder.url}
          folderId={folder.url}
          displayName={folder.displayName}
          isChecked={folder.isSync}
          color={folder.calendarColor._cdata}
          isShared={folder.isShared}
          isSharedOwner={folder.isSharedOwner}
          isDefault={folder.isDefault}
          isOrgShared={isOrgShared}
          isCalendarFolder={folder.folderType === 'calendar'}
          onCheckClick={onCheckClick}
          onFolderEdit={onFolderEdit}
          onShareClick={onShareClick}
          onDeleteClick={onDeleteClick}
        />
      )
    }

    const aListItems: React.ReactNode[] = [
      <div key="myCalendars" className={styles.myCalendars}>
        {i18n.t('calendar.folder.myCalendars')}
      </div>,
      ...myFolders.map(renderFolderItem),
    ]

    if (sharedFolders.length > 0) {
      aListItems.push(
        <div key="sharedCalendars" className={styles.sharedCalendars}>
          {i18n.t('calendar.folder.sharedCalendars')}
        </div>,
        ...sharedFolders.map(renderFolderItem),
      )
    }

    if (orgSharedFolders.length > 0) {
      aListItems.push(
        <div key="orgSharedCalendars" className={styles.orgSharedCalendars}>
          {i18n.t('calendar.folder.orgSharedCalendars')}
        </div>,
        ...orgSharedFolders.map(renderFolderItem),
      )
    }

    if (subscriptionFolders.length > 0) {
      aListItems.push(
        <div key="subscriptions" className={styles.myCalendars}>
          {i18n.t('calendar.folder.subscriptions')}
        </div>,
      )

      subscriptionFolders.forEach((folder) => {
        aListItems.push(
          <FolderListItem
            key={folder.url}
            folderId={folder.url}
            displayName={folder.displayName}
            isChecked={folder.isSync}
            color={folder.calendarColor._cdata}
            isSubscription
            onCheckClick={onCheckClick}
            onFolderEdit={onFolderEdit}
            onShareClick={onShareClick}
            onDeleteClick={onDeleteClick}
            onUnsubscribeClick={onUnsubscribeClick}
          />,
        )
      })
    }

    return aListItems
  }

  return <div className={styles.calendarFolderList}>{getFolderList()}</div>
}

export default FolderList
